import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description"; // generic doc
import TableChartIcon from "@mui/icons-material/TableChart"; // spreadsheet
import ImageIcon from "@mui/icons-material/Image"; // images
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"; // fallback
import DeleteIcon from "@mui/icons-material/Delete";
import { useError } from "../../Context/errorContext";
import config from "../../Config/config";
import { saveFiles } from "../../Controllers/projectController";
import Section from "./section";
import { options } from "../helpers";

const Attachments = ({
  allowedTypes,
  allowedExtensions,
  attachments,
  uid,
  refreshProject,
}) => {
  const [category, setCategory] = useState(null);
  const [uploads, setUploads] = useState([]);
  const { setAlert } = useError();

  function getFileIcon(type) {
    if (!type) return <InsertDriveFileIcon color="action" />;

    if (type === "application/pdf") return <PictureAsPdfIcon color="error" />;

    if (
      type === "application/msword" ||
      type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      return <DescriptionIcon color="primary" />;
    }

    if (
      type === "application/vnd.ms-excel" ||
      type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      type === "text/csv"
    ) {
      return <TableChartIcon color="success" />;
    }

    if (type.startsWith("image/")) {
      return <ImageIcon color="secondary" />;
    }

    return <InsertDriveFileIcon color="action" />;
  }

  function handleFiles(fileList, cat) {
    const files = Array.from(fileList);
    const MAX_MB = 10;

    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      const typeOk = allowedTypes?.has ? allowedTypes.has(file.type) : true; // if no validator passed, allow all
      const sizeOk = file.size <= MAX_MB * 1024 * 1024;

      if (!typeOk)
        errors.push(
          `${file.name}: unsupported file type (${file.type || "unknown"})`,
        );
      if (!sizeOk) errors.push(`${file.name}: exceeds ${MAX_MB} MB`);

      if (typeOk && sizeOk) {
        validFiles.push({
          id: `${Date.now()}`,
          file,
          category: cat ?? null,
          previewUrl: URL.createObjectURL(file),
        });
      }
    });

    if (errors.length) {
      console.error("File errors:", errors);
      alert(errors.join("\n"));
    }

    setUploads((prev) => [...prev, ...validFiles]);
    setCategory(null);
  }

  const removeById = (id) => {
    setUploads((prev) => {
      const next = [...prev];
      const item = next.find((u) => u.id === id);
      if (item.uid) {
        item.deleted = true;
        return next;
      }
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return next.filter((u) => u.id !== id);
    });
  };

  const save = async () => {
    if (uploads.length !== 0) {
      const success = await saveFiles(uid, uploads, setAlert);
      if (success) {
        setUploads([]);
        await refreshProject();
      }
    }
  };

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      uploads.forEach((p) => {
        if (p?.previewUrl) URL.revokeObjectURL(p.previewUrl);
      });
    };
  }, [uploads]);

  return (
    <Section title={"Project files"}>
      <Box sx={{ minWidth: 0 }}>
        {(attachments?.length ?? 0) === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No attachments yet
          </Typography>
        ) : (
          <Stack spacing={1}>
            {attachments.map((u) => {
              const url = `${config.serverBaseUrl}/projects/attachments/${uid}/${u.uid}/download/${u.original_name}`;
              return (
                <Link
                  key={u.attachment_id}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    justifyItems="start"
                    sx={{ minWidth: 0 }}
                  >
                    {getFileIcon(u.mime_type)}
                    <Typography
                      variant="body2"
                      color="text.primary"
                      sx={{ fontWeight: "bold" }}
                    >
                      {u.original_name}
                    </Typography>
                    {u.category && <Chip size="small" label={u.category} />}
                  </Stack>
                </Link>
              );
            })}
          </Stack>
        )}
        <Divider flexItem sx={{ pt: 2, mb: 2 }} />
        <Stack direction="row" spacing={1} alignItems="center" sx={{ pb: 3 }}>
          <Autocomplete
            size="small"
            options={options}
            value={category}
            onChange={(_, val) => setCategory(val)}
            sx={{ minWidth: 180 }}
            renderInput={(params) => (
              <TextField {...params} label="Select Type" />
            )}
          />

          <Button component="label" variant="contained" disabled={!category}>
            Add Attachment
            <input
              hidden
              type="file"
              accept={Array.from([...allowedTypes, ...allowedExtensions]).join(
                ",",
              )}
              onChange={(e) => {
                if (e.target.files?.length) {
                  handleFiles(e.target.files, category);
                  e.target.value = ""; // allow picking the same file again
                }
              }}
            />
          </Button>
        </Stack>

        {uploads.length !== 0 && (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.primary">
              In Preview
            </Typography>

            {uploads.map((u) => {
              const url = `${config.serverBaseUrl}/projects/attachments/${uid}/${u.uid}/download/${u.file.name}`;
              return (
                <Stack
                  key={u.id}
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  justifyItems="start"
                  sx={{ minWidth: 0 }}
                >
                  <Link
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.preventDefault();
                      window.open(url, "_blank", "noopener,noreferrer");
                    }}
                    underline="hover"
                    sx={{
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      justifyItems="start"
                      sx={{ minWidth: 0 }}
                    >
                      {getFileIcon(u.file.type)}
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{ fontWeight: "bold" }}
                      >
                        {u.file.name}
                      </Typography>
                      {u.category && <Chip size="small" label={u.category} />}
                    </Stack>
                  </Link>
                  <Tooltip title="Remove attachment">
                    <IconButton
                      aria-label={`Remove ${u.file.name}`}
                      size="small"
                      onClick={() => removeById(u.id)}
                    >
                      <DeleteIcon fontSize="small" color="error" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              );
            })}
            <Button variant="contained" onClick={(e) => save()}>
              Save Attachments
            </Button>
          </Stack>
        )}
      </Box>
    </Section>
  );
};

export default Attachments;
